// Check valves with mounting kit (owner, 2026-10-20: "Pull the details and dimensions for these check valves, add them to
// TraceCAD. For check valves in pipes over 50 mm we use these check valves with mounting kits. I just want a simplified
// drawing for them, nothing fancy; it just needs to draw a mounted check valve").
//
// SOURCE: Nozbart catalogue page 141, "PPRC pluggable swing check valve with mounting kit" (PPRC komple tak-cikar
// calpara cekvalf), the second table. PP body, EPDM o-rings, PN 10. The code has a colour letter in place of the dots
// (B, G, M or Y). The list price is the catalogue price in euro.
//
// WHAT IS EXACT AND WHAT IS NOT. H (the length of the whole assembly), L (each flange adaptor), Z (the valve body), D (the
// pipe outside diameter at the ends) and D4 (the flange outside diameter) are drawn at the catalogue values. The valve
// body diameter is D2 from the first table on the same page (the valve without the kit); for D225 and D280 that table has
// no row: the body diameter is the flange diameter D4 times the body/flange ratio of the sizes next to it (0.82 and 0.826). The flange thickness (0.4 x L), the two bolts and the
// flow arrow are drawn to proportion: a simplified drawing, not a shop drawing.
//
// THE DRAWING (one view; the valve is round, so it serves plan and side): the two adaptors with their flanges, the
// valve body between them, two bolts through the flanges with nuts, a dash-dot centre line, and an arrow in the body
// that points in the flow direction (inlet to outlet). Ends: [inlet, outlet], DN size, so the pipes of that size attach.
import type { Vec } from "../core/geometry";
import type { Entity, Style } from "../doc/entities";
import type { LibraryItem } from "./library";
import type { FittingPortData } from "../pipes/api";
import { CENTRE, OUTLINE } from "./fittings";

export const CHECK_VALVE_CATEGORY = "Pipe fittings · Check valves";

export interface CheckValveRow {
  /** Pipe outside diameter, mm (catalogue D). */
  D: number;
  /** Code; the dots take the colour letter (B, G, M or Y). */
  code: string;
  PN: number;
  /** Nominal size, mm: the TraceCAD pipe size. */
  DN: number;
  /** Catalogue list price, euro. */
  eur: number;
  /** Length of each flange adaptor, mm. */
  L: number;
  /** Width of the valve body, mm. */
  Z: number;
  /** Length of the whole assembly, end to end, mm. */
  H: number;
  /** Bore of the valve, mm. */
  D3: number;
  /** Flange outside diameter, mm. */
  D4: number;
  /** Valve body outside diameter, mm (first table, D2). */
  D2: number;
  /** True when D2 is estimated (no row in the first table). */
  D2estimated?: boolean;
}

/** Nozbart catalogue p. 141, second table (with mounting kit); D2 from the first table. */
export const CHECK_VALVE_ROWS: readonly CheckValveRow[] = [
  { D: 90, code: "TCC…-091", PN: 10, DN: 80, eur: 71.0, L: 51, Z: 28, H: 140, D3: 53, D4: 200, D2: 144 },
  { D: 110, code: "TCC…-111", PN: 10, DN: 100, eur: 85.2, L: 61, Z: 33, H: 165, D3: 71, D4: 220, D2: 164 },
  { D: 125, code: "TCC…-126", PN: 10, DN: 110, eur: 92.3, L: 69, Z: 35, H: 183, D3: 78.5, D4: 250, D2: 194 },
  { D: 140, code: "TCC…-141", PN: 10, DN: 125, eur: 106.5, L: 76, Z: 38, H: 200, D3: 88, D4: 250, D2: 194 },
  { D: 160, code: "TCC…-161", PN: 10, DN: 150, eur: 120.7, L: 86, Z: 42.5, H: 224.5, D3: 110, D4: 285, D2: 220 },
  { D: 200, code: "TCC…-201", PN: 10, DN: 175, eur: 178.92, L: 106, Z: 51, H: 275, D3: 139, D4: 340, D2: 275 },
  { D: 225, code: "TCC…-226", PN: 10, DN: 200, eur: 207.32, L: 119, Z: 54.5, H: 304.5, D3: 150, D4: 340, D2: 279, D2estimated: true },
  { D: 250, code: "TCC…-251", PN: 10, DN: 225, eur: 479.96, L: 131, Z: 57.5, H: 335.5, D3: 170, D4: 395, D2: 330 },
  { D: 280, code: "TCC…-281", PN: 10, DN: 250, eur: 596.4, L: 147, Z: 60.5, H: 374.5, D3: 195, D4: 397, D2: 328, D2estimated: true },
  { D: 315, code: "TCC…-316", PN: 10, DN: 300, eur: 837.8, L: 164, Z: 67, H: 411, D3: 218, D4: 465, D2: 380 }
];

/** What a check valve says about itself (group meta.valve), for the Inspector and a later parts list. */
export interface CheckValveInfo extends CheckValveRow {
  maker: "Nozbart";
  model: string;
  body: string;
  seals: string;
  source: string;
}

const round = (v: number) => Math.round(v * 1000) / 1000;
const rp = (p: Vec): Vec => ({ x: round(p.x), y: round(p.y) });

/** "Check valve · DN100 · D110 · mounting kit" */
export const checkValveName = (r: Pick<CheckValveRow, "DN" | "D">): string => `Check valve · DN${r.DN} · D${r.D} · mounting kit`;

/** The row for a pipe size (DN), or undefined. */
export const checkValveRowFor = (dn: number): CheckValveRow | undefined => CHECK_VALVE_ROWS.find((r) => r.DN === dn);

/** One check valve with mounting kit as a library item, centred on 0,0, the flow from left (inlet) to right (outlet). */
export function makeCheckValveItem(r: CheckValveRow): LibraryItem {
  const key = `checkvalve-${r.DN}`;
  let n = 0;
  const entities: Entity[] = [];
  const style = (centre = false): Style => structuredClone(centre ? CENTRE : OUTLINE);
  const line = (pts: Vec[], closed = false, centre = false) =>
    entities.push({ id: `${key}-${++n}`, type: "polyline", layerId: "fittings", locked: false, hidden: false, points: pts.map(rp), closed, style: style(centre) });
  const rect = (x0: number, y0: number, x1: number, y1: number) => line([{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }], true);

  const h = r.H / 2;
  const F = Math.round(0.4 * r.L); // flange thickness: drawn to proportion
  const gap = Math.max(0, (r.H - 2 * r.L - r.Z) / 2); // gasket between flange face and body
  const face = r.Z / 2 + gap; // x of each flange face
  // the two adaptors: the pipe part (width D) and the flange (width D4)
  for (const s of [-1, 1]) {
    const end = s * h;
    const flangeIn = s * (face + F);
    rect(Math.min(end, flangeIn), -r.D / 2, Math.max(end, flangeIn), r.D / 2);
    rect(Math.min(flangeIn, s * face), -r.D4 / 2, Math.max(flangeIn, s * face), r.D4 / 2);
  }
  // the valve body (with its gaskets) between the flange faces, and its faces
  rect(-face, -r.D2 / 2, face, r.D2 / 2);
  line([{ x: -r.Z / 2, y: -r.D2 / 2 }, { x: -r.Z / 2, y: r.D2 / 2 }]);
  line([{ x: r.Z / 2, y: -r.D2 / 2 }, { x: r.Z / 2, y: r.D2 / 2 }]);
  // two bolts through both flanges, with a nut on each end
  const by = (r.D2 / 2 + r.D4 / 2) / 2;
  // a nut fits between the body and the edge of the flange
  const nut = Math.max(6, Math.min(Math.round(0.08 * r.D4), Math.round(((r.D4 - r.D2) / 2) * 0.6)));
  for (const s of [-1, 1]) {
    const y = s * by;
    line([{ x: -(face + F + nut), y }, { x: face + F + nut, y }]);
    rect(-(face + F + nut), y - nut / 2, -(face + F), y + nut / 2);
    rect(face + F, y - nut / 2, face + F + nut, y + nut / 2);
  }
  // the flow arrow in the body: inlet (left) to outlet (right)
  const a = Math.min(r.D3 * 0.4, r.D2 * 0.25);
  line([{ x: -a * 0.6, y: -a / 2 }, { x: a * 0.6, y: 0 }, { x: -a * 0.6, y: a / 2 }], true);
  // the centre line
  line([{ x: -h, y: 0 }, { x: h, y: 0 }], false, true);

  const ports: FittingPortData[] = [
    { p: { x: -h, y: 0 }, dir: { x: -1, y: 0 }, size: r.DN },
    { p: { x: h, y: 0 }, dir: { x: 1, y: 0 }, size: r.DN }
  ];
  const info: CheckValveInfo = {
    ...r,
    maker: "Nozbart",
    model: "PPRC pluggable swing check valve with mounting kit",
    body: "PP",
    seals: "EPDM o-rings (not for petroleum, its derivatives or organic oils)",
    source: "Nozbart catalogue p. 141"
  };
  entities.push({
    id: `${key}-group`,
    type: "group",
    layerId: "fittings",
    locked: false,
    hidden: false,
    children: entities.map((e) => e.id),
    meta: { fitting: { kind: "checkvalve", size: r.DN }, valve: info, pivot: rp(ports[0]!.p), ports }
  });
  return {
    id: `builtin:${key}`,
    name: `${checkValveName(r)} · ${r.code}`,
    category: CHECK_VALVE_CATEGORY,
    entities,
    size: { w: r.H, h: r.D4 }, // the bolts and nuts stay inside the adaptors (0.4 L + nut < L for every row)
    units: "mm",
    updatedAt: "2026-10-20T00:00:00.000Z"
  };
}

let cache: LibraryItem[] | null = null;
/** The 10 check valves, DN80 to DN300. */
export function builtInCheckValves(): LibraryItem[] {
  if (!cache) cache = CHECK_VALVE_ROWS.map(makeCheckValveItem);
  return cache;
}

/** A check valve item for pipe size `dn`, or null when the catalogue has none. */
export function checkValveItemFor(dn: number): LibraryItem | null {
  const r = checkValveRowFor(dn);
  return r ? makeCheckValveItem(r) : null;
}
